import type { PermissionRequirement } from '@blpde/nx-layout';
export type { PermissionRequirement } from '@blpde/nx-layout';

export type PermissionMode = 'all' | 'any';

/**
 * ERP permission catalog for the FE.
 *
 * Two layers — do not mix them:
 * - **Property path** (TS): camelCase — `ERP_PERMISSIONS.salesQuotes.update`
 * - **Wire key** (string value): snake_case resource + action —
 *   `'sales_quotes.update'`. Matches master `permission_definitions.key`
 *   and backend `authzmw.RequireOrPass(..., "sales_quotes.update", ...)`.
 *
 * Always go through this object. Never hardcode wire keys (or camelCase
 * wire keys) at call sites — that is what produced the snake/camel mix.
 */
export const ERP_PERMISSIONS = {
  reporting: {
    view: 'reports.view',
  },
  salesQuotes: {
    read: 'sales_quotes.read',
    create: 'sales_quotes.create',
    update: 'sales_quotes.update',
    delete: 'sales_quotes.delete',
    convert: 'sales_quotes.convert',
    // Sets the per-record levels of one quote (BLP/nx#1771). Master decides it on
    // the quote itself, so the header button asks the backend for the record
    // (`useRecordPermissions`) rather than this type-level key. The same holds for
    // `share` on customers, suppliers, products and employees below.
    share: 'sales_quotes.share',
  },
  salesDocumentLines: {
    create: 'sales_document_lines.create',
    update: 'sales_document_lines.update',
    delete: 'sales_document_lines.delete',
  },
  salesDocumentAdditionalCharges: {
    create: 'sales_document_additional_charges.create',
    update: 'sales_document_additional_charges.update',
    delete: 'sales_document_additional_charges.delete',
  },
  salesDocumentAddresses: {
    create: 'sales_document_addresses.create',
    update: 'sales_document_addresses.update',
    delete: 'sales_document_addresses.delete',
  },
  salesDocumentPriceAdjustments: {
    create: 'sales_document_price_adjustments.create',
    update: 'sales_document_price_adjustments.update',
    delete: 'sales_document_price_adjustments.delete',
  },
  salesDocumentLineDiscounts: {
    create: 'sales_document_line_discounts.create',
    update: 'sales_document_line_discounts.update',
    delete: 'sales_document_line_discounts.delete',
  },
  customers: {
    read: 'customers.read',
    create: 'customers.create',
    update: 'customers.update',
    delete: 'customers.delete',
    share: 'customers.share',
  },
  // Vertragsverwaltung (I-274, CR 65). Wire keys match the generated authz
  // guards on /contract_partners and its addresses child.
  contractPartners: {
    read: 'contract_partners.read',
    create: 'contract_partners.create',
    update: 'contract_partners.update',
    delete: 'contract_partners.delete',
  },
  contractPartnerAddresses: {
    create: 'contract_partner_addresses.create',
    update: 'contract_partner_addresses.update',
    delete: 'contract_partner_addresses.delete',
  },
  // The contracts of a partner and their children (I-274 §6.1.3). Write keys
  // match the generated authz guards on /contracts and its children; `read` is
  // the route gate only — the backend leaves GET ungated and the assignment
  // lives in the master (O12, unverified for this module).
  contracts: {
    read: 'contracts.read',
    create: 'contracts.create',
    update: 'contracts.update',
    delete: 'contracts.delete',
  },
  contractLines: {
    create: 'contract_lines.create',
    update: 'contract_lines.update',
    delete: 'contract_lines.delete',
  },
  contractLineRates: {
    create: 'contract_line_rates.create',
    update: 'contract_line_rates.update',
    delete: 'contract_line_rates.delete',
  },
  // The text section generates and edits, never deletes: v1 keeps exactly one
  // main_contract text per contract, and "generate again" writes a new version
  // rather than removing the old one. `create` also gates the generate route —
  // it IS a create, just one whose body the server writes.
  contractTexts: {
    create: 'contract_texts.create',
    update: 'contract_texts.update',
  },
  // Partner catalogues (I-2631). Reads gate the mask routes; writes mirror
  // the REST and GraphQL keys.
  salutations: {
    read: 'salutations.read',
    create: 'salutations.create',
    update: 'salutations.update',
    delete: 'salutations.delete',
  },
  academicTitles: {
    read: 'academic_titles.read',
    create: 'academic_titles.create',
    update: 'academic_titles.update',
    delete: 'academic_titles.delete',
  },
  industries: {
    read: 'industries.read',
    create: 'industries.create',
    update: 'industries.update',
    delete: 'industries.delete',
  },
  contractTypes: {
    read: 'contract_types.read',
    create: 'contract_types.create',
    update: 'contract_types.update',
    delete: 'contract_types.delete',
  },
  interestAreas: {
    read: 'interest_areas.read',
    create: 'interest_areas.create',
    update: 'interest_areas.update',
    delete: 'interest_areas.delete',
  },
  customerAddressSourceGroups: {
    read: 'customer_address_source_groups.read',
    create: 'customer_address_source_groups.create',
    update: 'customer_address_source_groups.update',
    delete: 'customer_address_source_groups.delete',
  },
  postalCodes: {
    read: 'postal_codes.read',
    create: 'postal_codes.create',
    update: 'postal_codes.update',
    delete: 'postal_codes.delete',
  },
  // Quality, service and production catalogues (I-2764): one key per operation.
  priorities: {
    read: 'priorities.read',
    create: 'priorities.create',
    update: 'priorities.update',
    delete: 'priorities.delete',
  },
  packagingTypes: {
    read: 'packaging_types.read',
    create: 'packaging_types.create',
    update: 'packaging_types.update',
    delete: 'packaging_types.delete',
  },
  skills: {
    read: 'skills.read',
    create: 'skills.create',
    update: 'skills.update',
    delete: 'skills.delete',
  },
  scrapReasons: {
    read: 'scrap_reasons.read',
    create: 'scrap_reasons.create',
    update: 'scrap_reasons.update',
    delete: 'scrap_reasons.delete',
  },
  defectCauses: {
    read: 'defect_causes.read',
    create: 'defect_causes.create',
    update: 'defect_causes.update',
    delete: 'defect_causes.delete',
  },
  complaintReasons: {
    read: 'complaint_reasons.read',
    create: 'complaint_reasons.create',
    update: 'complaint_reasons.update',
    delete: 'complaint_reasons.delete',
  },
  // The article catalogues (I-2763): one key per operation.
  qualities: {
    read: 'qualities.read',
    create: 'qualities.create',
    update: 'qualities.update',
    delete: 'qualities.delete',
  },
  hazardClasses: {
    read: 'hazard_classes.read',
    create: 'hazard_classes.create',
    update: 'hazard_classes.update',
    delete: 'hazard_classes.delete',
  },
  priceUnits: {
    read: 'price_units.read',
    create: 'price_units.create',
    update: 'price_units.update',
    delete: 'price_units.delete',
  },
  attributes: {
    read: 'attributes.read',
    create: 'attributes.create',
    update: 'attributes.update',
    delete: 'attributes.delete',
  },
  // Class-A follow-up catalogues (I-2785): one key per operation.
  partnerTags: {
    read: 'partner_tags.read',
    create: 'partner_tags.create',
    update: 'partner_tags.update',
    delete: 'partner_tags.delete',
  },
  declineReasons: {
    read: 'decline_reasons.read',
    create: 'decline_reasons.create',
    update: 'decline_reasons.update',
    delete: 'decline_reasons.delete',
  },
  sortGroups: {
    read: 'sort_groups.read',
    create: 'sort_groups.create',
    update: 'sort_groups.update',
    delete: 'sort_groups.delete',
  },
  // The global language catalogue (I-3096): one key per operation.
  languages: {
    read: 'languages.read',
    create: 'languages.create',
    update: 'languages.update',
    delete: 'languages.delete',
  },
  companies: {
    read: 'companies.read',
    create: 'companies.create',
    update: 'companies.update',
    delete: 'companies.delete',
  },
  // Company children (CR #48). Wire keys match the generated authz guards on
  // /companies/:id/communication_channels | bank_accounts | accounting_settings.
  companyCommunicationChannels: {
    create: 'company_communication_channels.create',
    update: 'company_communication_channels.update',
    delete: 'company_communication_channels.delete',
  },
  companyBankAccounts: {
    create: 'company_bank_accounts.create',
    update: 'company_bank_accounts.update',
    delete: 'company_bank_accounts.delete',
  },
  companyAccountingSettings: {
    update: 'company_accounting_settings.update',
    delete: 'company_accounting_settings.delete',
  },
  companyEmployeeAssignments: {
    create: 'company_employee_assignments.create',
    update: 'company_employee_assignments.update',
    delete: 'company_employee_assignments.delete',
  },
  employees: {
    read: 'employees.read',
    create: 'employees.create',
    update: 'employees.update',
    delete: 'employees.delete',
    share: 'employees.share',
  },
  employeeSkills: {
    create: 'employee_skills.create',
    update: 'employee_skills.update',
    delete: 'employee_skills.delete',
  },
  employeeAbsences: {
    /**
     * The personnel-desk READ key — `GET /v1/employees/:employeeKey/absences`
     * and its `/:id` sibling. Enforced since I-1773, on EVERY read path: the
     * two REST GETs, and — via the catalog resource's `AuthzKey` — `POST
     * /v1/reads`, GraphQL and any tree edge reaching the rows. A caller
     * without this key gets 403, so gate the desk views on it rather than
     * letting them render into an error.
     *
     * ⚠️ Necessary, not sufficient (I-1463): the REST GETs also need the caller
     * to look after the employee — as their manager, their department's
     * manager or with `employees.oversee` — or to read their own rows; else 403
     * `employee_not_managed`. The tree reads (`/v1/reads`, GraphQL, edges) need
     * `employees.oversee` beside this key. The key gates the view; the server's
     * refusal for a single employee is shown, not guessed.
     *
     * Until I-1773 the routes mounted ungated and any signed-in account could
     * read a colleague's sick notes by naming a foreign employee id (I-1755).
     * The key is seeded by master update 32; granting it to a role is an admin
     * decision, not a module's (I-1718), so the desk stays 403 until someone
     * does.
     *
     * Its own key rather than `create` reused: "may see the workforce's
     * absences" is the desk's view and belongs to more people than "may record
     * one for anyone".
     *
     * ⚠️ NOT the key for the employee's own surface — that is `request` below.
     */
    read: 'employee_absences.read',
    create: 'employee_absences.create',
    update: 'employee_absences.update',
    delete: 'employee_absences.delete',
    /**
     * Gates the absence status machine — approve / reject / cancel, the desk's
     * withdraw of a pending request (#2912) and the sick-note report verb
     * (I-1251). Distinct from `update` on purpose: the status is no longer a
     * field on the generic PATCH, so editing an absence and deciding on one are
     * separate rights. Since I-1463 the verbs also need the caller to look
     * after the employee (manager, department manager or `employees.oversee`),
     * else 422 `employee_not_managed`; the caller's own absence stays 403
     * `self_approval`.
     */
    approve: 'employee_absences.approve',
    /**
     * Gates the employee's OWN surface — `/v1/me/absences`: listing, filing and
     * withdrawing one's own requests, and cancelling one's own approved absence
     * before it starts (I-1166, #2812). Distinct from `create`, which is the
     * personnel desk's right to record an absence for ANY employee id; this one
     * never reaches another person's rows.
     */
    request: 'employee_absences.request',
  },
  /**
   * Day results of the attendance journal (#2532 decision 3). `approve` is the
   * superior's right to REVIEW a day — today „Fehler klären" (clarify a review
   * item) and the day note, later the day approval (S6) under the same key.
   * Since I-1463 the key is not enough on its own: the caller must also look
   * after the employee (manager, department manager or `employees.oversee`)
   * or review their own day, else 422 `employee_not_managed`. Reading the rows
   * stays with `attendanceEvents.read`.
   */
  attendanceDailyRecords: { approve: 'attendance_daily_records.approve' },
  /**
   * Attendance journal (I-1440). The axis is WHOSE time, not read vs. write
   * (#1439 decision 2): on their own row an employee captures and amends
   * without a request; on a colleague's row only the correction rights admit.
   *
   * Seeded by master update 40. No role carries any of them by default — see
   * the migration's comment for why that is an open decision and not an
   * oversight.
   */
  attendanceEvents: {
    /**
     * Gates the employee's OWN surface — `/v1/me/attendance/…`: reading own
     * events, the four capture verbs, the reversal, and the own day results of
     * #1464. One key for the whole surface, reads included, exactly like
     * `employee_absences.request` (I-1166 decision 2).
     *
     * ⚠️ Necessary but not sufficient: the surface also requires the acting
     * employee's `has_time_recording` flag, which the server checks. A caller
     * holding this key on an employee who books no time gets 403
     * `time_recording_disabled` — gate the entry point on the key, and render
     * the server's refusal rather than guessing the flag in the browser.
     */
    capture: 'attendance_events.capture',
    /**
     * Reading ANOTHER employee's journal — the personnel desk's range route.
     * Since I-1463 only of an employee the caller looks after (manager,
     * department manager or `employees.oversee`), else 403
     * `employee_not_managed`.
     */
    read: 'attendance_events.read',
    /** Booking on another employee's journal (`origin = 'correction'`). */
    create: 'attendance_events.create',
    /** Reversing another employee's booking. Its own key, not `create`. */
    reverse: 'attendance_events.reverse',
    /**
     * The terminal's right (I-2098): the signed-in operator books for the
     * employee the PIN resolved (`origin = 'terminal'`). Gates the terminal
     * route's entry point; every /v1/terminal/… route enforces it server-side.
     */
    captureOnBehalf: 'attendance_events.capture_on_behalf',
  },
  // I-896. Mirrors what the child route enforces server-side
  // (backend/erp/api/public/modules/employees/signatures/controller.go:41-44).
  //
  // Declared but deliberately NOT used as a pre-gate, and that is settled, not
  // pending (maintainer's decision of 2026-08-25): whoever may edit a personnel
  // record may also save its signature — one circle of people, not two. The
  // keys are therefore granted alongside `employees.update` in the Master
  // mirror, and the signature card gates on the mask's own
  // `provideFieldEditable` (`employees.update`) like every other input in it.
  //
  // Gating the card on these keys as well would buy nothing and cost something:
  // it would disable the field for anyone whose grant has not been rolled out
  // yet — a dead end with nothing on screen to explain it — while the server
  // remains the authority either way. Round 4 recommended the opposite (major 2,
  // "constants and gate on them, SkillsSection pattern"); round 3 recommended
  // this (`useFieldEditable()` in the card), and this is the one that holds once
  // the two circles are the same.
  //
  // They stay declared because the wire contract is real and the next reader
  // should find it named here rather than only in a Go route.
  employeeSignatures: {
    create: 'employee_signatures.create',
    update: 'employee_signatures.update',
    delete: 'employee_signatures.delete',
  },
  suppliers: {
    read: 'suppliers.read',
    create: 'suppliers.create',
    update: 'suppliers.update',
    delete: 'suppliers.delete',
    share: 'suppliers.share',
  },
  products: {
    read: 'products.read',
    create: 'products.create',
    update: 'products.update',
    delete: 'products.delete',
    share: 'products.share',
  },
  jurisdictions: {
    read: 'jurisdictions.read',
    create: 'jurisdictions.create',
    update: 'jurisdictions.update',
    delete: 'jurisdictions.delete',
  },
  // The break scale is a composition child, so it has no read permission of its
  // own — reading it rides on the parent (the generated child list route is
  // ungated, like every other child list).
  jurisdictionBreakTiers: {
    create: 'jurisdiction_break_tiers.create',
    update: 'jurisdiction_break_tiers.update',
    delete: 'jurisdiction_break_tiers.delete',
  },
  // Maschinen (I-1046). `read` gates the Ausfallzeiten widget the way
  // `jurisdictions.read` gates the Pausenstufen; the mask routes themselves
  // derive theirs from the spec's basePath (`resourcePermission`).
  machines: {
    read: 'machines.read',
    create: 'machines.create',
    update: 'machines.update',
    delete: 'machines.delete',
  },
  // The downtime list is a composition child of the machine: no read permission
  // of its own, reading rides on the parent (see jurisdictionBreakTiers).
  machineDowntimeEvents: {
    create: 'machine_downtime_events.create',
    update: 'machine_downtime_events.update',
    delete: 'machine_downtime_events.delete',
  },
  // Positionstypen (I-3485): names per language and directions are composition
  // children of the type, so they have no read key of their own and the type has
  // none either (reads are ungated). The write keys gate the section actions.
  documentLineTypeTranslations: {
    create: 'document_line_type_translations.create',
    update: 'document_line_type_translations.update',
    delete: 'document_line_type_translations.delete',
  },
  documentLineTypeDirections: {
    create: 'document_line_type_directions.create',
    update: 'document_line_type_directions.update',
    delete: 'document_line_type_directions.delete',
  },
  // Werkzeuge (I-1047). `read` gates the two child-section widgets the way
  // `machines.read` gates the Ausfallzeiten; the mask routes derive theirs from
  // the spec's basePath (`resourcePermission`).
  tools: {
    read: 'tools.read',
    create: 'tools.create',
    update: 'tools.update',
    delete: 'tools.delete',
  },
  // Qualifizierung and Artikel are composition children of the tool: no read
  // permission of their own, reading rides on the parent.
  toolSkills: {
    create: 'tool_skills.create',
    update: 'tool_skills.update',
    delete: 'tool_skills.delete',
  },
  toolProducts: {
    create: 'tool_products.create',
    update: 'tool_products.update',
    delete: 'tool_products.delete',
  },
  // The USt strand (I-1473 … I-1476). The write routes have gated on these keys
  // since I-1473, but nothing declared them here and nothing DEFINED them in
  // master either, so Admin could not grant them. They arrive together with the
  // mask: master's boot catalog (`backend/master/core/seed/catalog.go`) defines
  // them, and this block mirrors it.
  taxRates: {
    read: 'tax_rates.read',
    create: 'tax_rates.create',
    update: 'tax_rates.update',
    delete: 'tax_rates.delete',
  },
  // The masterdata masks of currencies, countries and number series and the
  // company's ERP slots (I-2273). The routes have gated on these keys since
  // their modules were generated; master's boot catalog defines them, and these
  // blocks mirror it. The mask routes derive their read key from the spec's
  // basePath (`resourcePermission`), which yields the same strings.
  currencies: {
    read: 'currencies.read',
    create: 'currencies.create',
    update: 'currencies.update',
    delete: 'currencies.delete',
  },
  // Read inside the currency mask, so no read of its own.
  exchangeRates: {
    create: 'exchange_rates.create',
    update: 'exchange_rates.update',
    delete: 'exchange_rates.delete',
  },
  countries: {
    read: 'countries.read',
    create: 'countries.create',
    update: 'countries.update',
    delete: 'countries.delete',
  },
  // The Abteilungsleiter of a department (BLP/nx#1463 decision 14): the child
  // routes `/departments/:departmentKey/managers` check these keys.
  departmentManagers: {
    create: 'department_managers.create',
    update: 'department_managers.update',
    delete: 'department_managers.delete',
  },
  // The EU VAT territory periods of a country (#2270). A composition child, so
  // no read of its own — the section rides on the country's read, like
  // `jurisdictionBreakTiers` above.
  countryEuVatTerritoryPeriods: {
    create: 'country_eu_vat_territory_periods.create',
    update: 'country_eu_vat_territory_periods.update',
    delete: 'country_eu_vat_territory_periods.delete',
  },
  // The account framework bindings of a country, a composition child as well.
  countryAccountFrameworkBindings: {
    create: 'country_account_framework_bindings.create',
    update: 'country_account_framework_bindings.update',
    delete: 'country_account_framework_bindings.delete',
  },
  // The company's ERP detail slot; it rides on `companies.read`.
  companyDetails: {
    update: 'company_details.update',
    delete: 'company_details.delete',
  },
  // A standard master-data resource since #2671: `read` gates its routes,
  // numbering's verbs check the write keys.
  numberSeries: {
    read: 'number_series.read',
    create: 'number_series.create',
    update: 'number_series.update',
  },
  // Composition children, so no read of their own — the section rides on the
  // parent's read, like `jurisdictionBreakTiers` above.
  taxRateCases: {
    create: 'tax_rate_cases.create',
    update: 'tax_rate_cases.update',
    delete: 'tax_rate_cases.delete',
  },
  taxReportCodes: {
    read: 'tax_report_codes.read',
    create: 'tax_report_codes.create',
    update: 'tax_report_codes.update',
    delete: 'tax_report_codes.delete',
  },
  taxReportCodeYears: {
    create: 'tax_report_code_years.create',
    update: 'tax_report_code_years.update',
    delete: 'tax_report_code_years.delete',
  },
  taxReportCodeComponents: {
    create: 'tax_report_code_components.create',
    update: 'tax_report_code_components.update',
    delete: 'tax_report_code_components.delete',
  },
  // The Kontenwerk (I-2106). The write routes of `ledger_accounts` have gated on
  // these keys since I-1479 and the framework selection (#1478) on `.create`,
  // but master defined none of them, so Admin could not grant them. They arrive
  // with the Sachkonten mask, defined in master's boot catalog like the tax keys.
  ledgerAccounts: {
    read: 'ledger_accounts.read',
    create: 'ledger_accounts.create',
    update: 'ledger_accounts.update',
    delete: 'ledger_accounts.delete',
  },
  // The statutory sections of the Sachkonto mask (I-2551): the statutory
  // mapping and the statutory account code (GoBD R9) of an account, both
  // composition children of `ledger_accounts` (#1480). No read of their own —
  // the sections ride on `ledger_accounts.read`, like `taxRateCases` above. Defined
  // in master's boot catalog next to the ledger account keys.
  ledgerAccountStatutoryMappings: {
    create: 'ledger_account_statutory_mappings.create',
    update: 'ledger_account_statutory_mappings.update',
    delete: 'ledger_account_statutory_mappings.delete',
  },
  ledgerAccountStatutoryAccountCodes: {
    create: 'ledger_account_statutory_account_codes.create',
    update: 'ledger_account_statutory_account_codes.update',
    delete: 'ledger_account_statutory_account_codes.delete',
  },
  // The Kontenrahmen display (I-2106) is read-only by decision: frameworks ship
  // with the seed and are not maintained in the mask. Only the read key is
  // defined in master's catalog; the write routes keep their generated keys.
  accountFrameworks: {
    read: 'account_frameworks.read',
  },
  // Geschäftsjahre and their posting periods (C2 #1483, mask C7b #2670), keys
  // defined in master's boot catalog with C2. `read` gates the mask only (the
  // module is not read-gated in the backend); `create` is the action „Geschäftsjahr
  // anlegen", `update` the year's PATCH and „Perioden neu einteilen", `delete`
  // the year's delete. Setting and lifting a posting lock — year or period — is
  // a decision with its own key, never `update` (decision F18 of #2668).
  fiscalYears: {
    read: 'fiscal_years.read',
    create: 'fiscal_years.create',
    update: 'fiscal_years.update',
    delete: 'fiscal_years.delete',
  },
  postingPeriods: {
    lock: 'posting_periods.lock',
  },
  // Accounting phase 2, the posting core (#3193, #3116 §14.1), keys defined in
  // master's boot catalog. Capturing and posting are two rights (F-F14): the
  // capture keys capture, `postingBatches.post` posts a batch. There is no
  // right "finalize" — NX finalizes on posting (F-A4). Checked per set of
  // books. The screens using them come with T1b–T4b (#3196, #3198, #3200,
  // #3202), the reversal with T7.
  postingBatches: {
    read: 'posting_batches.read',
    create: 'posting_batches.create',
    update: 'posting_batches.update',
    delete: 'posting_batches.delete',
    post: 'posting_batches.post',
    // Completing a batch whose records were all posted one by one costs the
    // posting right too (#3416).
    complete: 'posting_batches.post',
  },
  postingCaptures: {
    read: 'posting_captures.read',
    create: 'posting_captures.create',
    update: 'posting_captures.update',
    delete: 'posting_captures.delete',
    // Posting one record on its own ("Buchen einzeln", #3416).
    post: 'posting_batches.post',
  },
  postings: {
    read: 'postings.read',
    reverse: 'postings.reverse',
  },
  productSalesPriceTiers: {
    create: 'product_sales_price_tiers.create',
    update: 'product_sales_price_tiers.update',
    delete: 'product_sales_price_tiers.delete',
  },
  productSuppliers: {
    read: 'product_suppliers.read',
    create: 'product_suppliers.create',
    update: 'product_suppliers.update',
    delete: 'product_suppliers.delete',
  },
  productSupplierPriceTiers: {
    create: 'product_supplier_price_tiers.create',
    update: 'product_supplier_price_tiers.update',
    delete: 'product_supplier_price_tiers.delete',
  },
  priceLists: {
    read: 'price_lists.read',
    create: 'price_lists.create',
    update: 'price_lists.update',
    delete: 'price_lists.delete',
  },
  priceListItems: {
    read: 'price_list_items.read',
    create: 'price_list_items.create',
    update: 'price_list_items.update',
    delete: 'price_list_items.delete',
  },
  priceCampaigns: {
    read: 'price_campaigns.read',
    create: 'price_campaigns.create',
    update: 'price_campaigns.update',
    delete: 'price_campaigns.delete',
  },
  contacts: {
    read: 'contacts.read',
    create: 'contacts.create',
    update: 'contacts.update',
    delete: 'contacts.delete',
  },
  /**
   * Section 2 of the contact mask (I-1406). Own keys rather than the parent
   * `contacts.*`: the generated controller gates
   * POST/PATCH/DELETE /contacts/:contactKey/assignments on exactly these, so a
   * button gated on the parent would offer what the server refuses. No `read` —
   * the collection route carries no permission, same as the other child grids.
   */
  contactAssignments: {
    create: 'contact_assignments.create',
    update: 'contact_assignments.update',
    delete: 'contact_assignments.delete',
  },
  /**
   * A contact's addresses (`contact_addresses`, the Stammadresse is its `main`
   * slot). Own keys for the same reason as the assignments: the generated
   * controller gates POST/PATCH/DELETE /contacts/:contactKey/addresses on exactly
   * these — the partner contact card (I-2245) creates or patches the Stammadresse
   * through them.
   */
  contactAddresses: {
    create: 'contact_addresses.create',
    update: 'contact_addresses.update',
    delete: 'contact_addresses.delete',
  },
  tasks: {
    read: 'tasks.read',
    create: 'tasks.create',
    update: 'tasks.update',
    delete: 'tasks.delete',
  },
  projects: {
    read: 'projects.read',
    create: 'projects.create',
    update: 'projects.update',
    delete: 'projects.delete',
  },
  // CRM Kundenakte (I-622). Catalog keys live in master permission_definitions
  // (update_customer_files_permissions). Superadmin `*` still grants all of them.
  customerFiles: {
    read: 'customer_files.read',
    create: 'customer_files.create',
    update: 'customer_files.update',
    delete: 'customer_files.delete',
  },
  // F3 holiday calendars (I-1200). Catalog keys live in master
  // permission_definitions (update_holiday_calendar_permissions).
  holidayCalendars: {
    read: 'holiday_calendars.read',
    create: 'holiday_calendars.create',
    update: 'holiday_calendars.update',
    delete: 'holiday_calendars.delete',
  },
  // Record category schemas with their categories (I-2593), maintained in the
  // masterdata mask. The categories are written through the schema's own
  // action (`PATCH /record_category_schemas/:id/bdms`), so `update` covers
  // them; assigning a schema to a record stays `bdms.assign_category`.
  recordCategorySchemas: {
    read: 'record_category_schemas.read',
    create: 'record_category_schemas.create',
    update: 'record_category_schemas.update',
    delete: 'record_category_schemas.delete',
  },
  // B-DMS verbs, defined in master's boot catalog. Only the key a screen checks
  // is mirrored here. `assignCategory` gates assigning a record category schema
  // to a record and removing the assignment again (`assignCategorySchema` /
  // `unassignCategorySchema`); the server refuses both without it, so the
  // assignment dialog and „Zuordnung entfernen“ check it first (I-2593).
  bdms: {
    createEntry: 'bdms.create_entry',
    putAttachmentContent: 'bdms.put_attachment_content',
    assignCategory: 'bdms.assign_category',
  },
  // The day rows of a calendar (I-1200). Only write keys: the generated child
  // routes gate create/update/delete, while GET is ungated like the parent's.
  // Found on 24.08.2026 - all three were orphans, guarded but in no catalog.
  holidayCalendarDays: {
    create: 'holiday_calendar_days.create',
    update: 'holiday_calendar_days.update',
    delete: 'holiday_calendar_days.delete',
  },
  // F4 company deviations (I-1364). Catalog keys live in master
  // permission_definitions (update_company_deviation_permissions, update 33).
  // All four exist: create/update/delete were already enforced by the generated
  // child routes and the GraphQL mutations with nothing behind them, and read
  // gates the hand-mounted inheritance route
  // GET /v1/companies/:id/effective_deviations.
  companyDeviations: {
    read: 'company_deviations.read',
    create: 'company_deviations.create',
    update: 'company_deviations.update',
    delete: 'company_deviations.delete',
  },
  // F2 working time models (I-1372). Write keys ONLY — and that mirrors the
  // backend rather than trimming it: `api/modules/working_time_models`
  // registers both GET routes UNGATED, so a `working_time_models.read` simply
  // does not exist and inventing one here would gate a door the server leaves
  // open.
  //
  // ⚠️ None of these six is in the master permission catalog yet. #1096 emitted
  // the guards but no `backend/master/updates/**` recipe seeds them, so today
  // only a superadmin `*` satisfies them and nobody can be GRANTED one. The
  // mask therefore renders its write actions disabled for every normal user
  // until that recipe exists — tracked as the open Berechtigungskonzept of
  // #1020, not silently worked around here.
  workingTimeModels: {
    create: 'working_time_models.create',
    update: 'working_time_models.update',
    delete: 'working_time_models.delete',
  },
  // The seven day rows of a model (I-1372). Same shape and same caveat as the
  // parent: GET ungated, writes guarded, catalog entry missing.
  workingTimeModelWeekdays: {
    create: 'working_time_model_weekdays.create',
    update: 'working_time_model_weekdays.update',
    delete: 'working_time_model_weekdays.delete',
  },
  // Which employee works to which model, with its own validity (I-1372).
  workingTimeModelAssignments: {
    create: 'working_time_model_assignments.create',
    update: 'working_time_model_assignments.update',
    delete: 'working_time_model_assignments.delete',
  },
  // Terminal maintenance (#2131, keys named on #2150), defined in master's boot
  // catalog. `read` gates the Terminals and Unterbrechungsgründe masks — the
  // routes derive it from the spec's basePath (`resourcePermission`), which
  // yields the same strings — and the write keys mirror the generated write
  // routes. Not the terminal's own right: booking is
  // `attendanceEvents.captureOnBehalf`.
  terminals: {
    read: 'terminals.read',
    create: 'terminals.create',
    update: 'terminals.update',
    delete: 'terminals.delete',
  },
  interruptionReasons: {
    read: 'interruption_reasons.read',
    create: 'interruption_reasons.create',
    update: 'interruption_reasons.update',
    delete: 'interruption_reasons.delete',
  },
  // B-DMS activities (I-1325). completeForOthers lets the completion verbs act
  // on an assigned-employee row that is neither the caller's own nor covered by
  // them; the server checks it again (completeBdms / reopenBdms authorizer).
  recordActivities: {
    completeForOthers: 'record_activities.complete_for_others',
  },
  // Covers on a row that is not the caller's own need this grant; the row's
  // own employee manages its covers without it (addBdmsCover / removeBdmsCover
  // authorizer). The server checks it again.
  recordActivityAssignedEmployees: {
    update: 'record_activity_assigned_employees.update',
  },
  // Central comments (I-838). Wire keys match generated CRUD + hand system API.
  recordComments: {
    read: 'record_comments.read',
    create: 'record_comments.create',
    update: 'record_comments.update',
    delete: 'record_comments.delete',
  },
  // Abrechnungslauf / billing run (I-1499, run mask S2). `create`/`update` match
  // the generated authz guards on /billing_runs (backend/erp/api/public/modules/billing_runs/
  // controller.go); `read` is the route gate only, like `contracts.read` above —
  // GET is ungated (controller.go and the hand-written entries_routes.go both
  // mount it bare), and the FE key controls only who sees the mask. `delete` is
  // deliberately absent: no screen in this slice offers it.
  billingRuns: {
    read: 'billing_runs.read',
    create: 'billing_runs.create',
    update: 'billing_runs.update',
  },
} as const;

export function permissionKeys(requirement: PermissionRequirement): string[] {
  if (!requirement) return [];
  return typeof requirement === 'string' ? [requirement] : [...requirement];
}

export function permissionBaseFromPath(path: string): string {
  return path.replace(/^\/+/, '').replace(/\/+$/, '');
}

export function resourcePermission(pathOrBase: string, action: string): string {
  return `${permissionBaseFromPath(pathOrBase)}.${action}`;
}
